import {
  type ActivityFlowModel,
  type ActivityModel,
  type EvaluationDirectionType,
  type IndicatorModel,
  type IndicatorType,
  type IndicatorsModel,
  type TermType,
  TermTypeLabelModifier,
  type dataNatureType as dataNatureTypeEnum,
} from '@/features/project/projects/_models/activity';
import { t } from '@/i18n';
import {
  FALLBACK_TEXT,
  formatFraction,
  formatNegativeValue,
  formatNumber,
  getCurrentFiscalYear,
  removeComma,
} from '@/utils';
import type { z } from 'zod';

type Activity = Pick<z.infer<typeof ActivityModel>, 'sortOrder'>;
type Indicator = Pick<
  z.infer<typeof IndicatorModel>,
  'type' | 'sortOrder' | 'term'
>;

export const getNameWithSortOrder = (
  sortOrder: number | null,
  name: string,
): string => `${sortOrder ? `${sortOrder}: ` : ''}${name}`;

export const getNodeName = (
  activity: Activity | null,
  indicator: Indicator | null,
): string => {
  if (activity) {
    return getNameWithSortOrder(activity.sortOrder, t('activity'));
  }

  if (indicator) {
    switch (indicator.type) {
      case 'output':
        return getNameWithSortOrder(indicator.sortOrder, t('output'));
      case 'outcome':
        return getNameWithSortOrder(
          indicator.sortOrder,
          `${indicator.term ? TermTypeLabelModifier[indicator.term] : ''}${t(
            'outcome',
          )}`,
        );
    }
  }

  return '';
};

export const getIndicatorSectionTitle = (
  type: IndicatorType,
  term: TermType | null,
) => {
  return type === 'outcome'
    ? t('outcome-with-term', {
        term: term ? TermTypeLabelModifier[term] : '',
      })
    : t('activity-targets-and-results');
};

export const getIndicatorTitleLabel = (type?: IndicatorType) => {
  return type === 'outcome' ? t('outcome-objective') : t('activities-target');
};
export const getIndicatorDescriptionLabel = (type?: IndicatorType) => {
  return type === 'outcome' ? t('outcome-indicator') : t('output-indicator');
};

type TableDataType = { label: string } & Record<string, string | number | null>;

export const convertActivityAndResultTargetTableDataValue = (
  value: string | number | undefined | null,
) =>
  typeof value === 'string'
    ? value === FALLBACK_TEXT
      ? FALLBACK_TEXT
      : formatNegativeValue(value)
    : value;

export const rateByEvaluationDirection = (indicator: {
  targetValue: number | null;
  actualValue: number | null | undefined;
  evaluationDirection: EvaluationDirectionType | null | undefined;
}): number | null => {
  if (
    indicator.actualValue === null ||
    indicator.actualValue === undefined ||
    indicator.targetValue === null
  ) {
    return null;
  }

  if (indicator.actualValue === 0 && indicator.targetValue === 0) {
    return 100;
  }

  if (indicator.evaluationDirection === 'downward') {
    if (indicator.actualValue === 0) {
      return null;
    }

    const rate = formatFraction(indicator.targetValue, indicator.actualValue, {
      maximumFractionDigits: 1,
      hideUnit: true,
    });

    return Number(removeComma(rate));
  }

  if (indicator.evaluationDirection === 'upward') {
    if (indicator.targetValue === 0) {
      return null;
    }

    const rate = formatFraction(indicator.actualValue, indicator.targetValue, {
      maximumFractionDigits: 1,
      hideUnit: true,
    });

    return Number(removeComma(rate));
  }

  return null;
};

const unique = <T>(array: T[]): T[] => [...new Set(array)];

const fillYearsUpToMaxLength = ({
  requiredYears,
  allYears,
  maxLength,
  lastYear,
}: {
  requiredYears: number[];
  allYears: number[];
  maxLength: number;
  lastYear: number | undefined;
}): number[] => {
  const additionalYears = allYears.filter(
    (year) => !requiredYears.includes(year),
  );

  const resultYears = [
    ...requiredYears,
    ...additionalYears.slice(0, maxLength - requiredYears.length),
  ].sort((a, b) => a - b);

  if (lastYear) {
    return resultYears.filter((year) => year <= lastYear);
  }

  return resultYears;
};

// 年度設定に関する定数
const LEDGER_YEAR_SETTINGS = {
  /** 表示する最大年度数 */
  MAX_SHOW_YEARS: 10,
  /** 過去の表示年度数 */
  PAST_YEAR_LENGTH: 3,
  /** 未来の表示年度数（最終年度は含めない） */
  FUTURE_YEAR_LENGTH: 5,
  /** デフォルトの表示年度数（現年度と最終年度） */
  DEFAULT_SHOW_YEAR_LENGTH: 2,
} as const;

type YearGroups = {
  targetPastYears: number[];
  targetFutureYears: number[];
  latestTargetYear: number;
  sortedAllYears: number[];
};

const getYearGroups = (
  data: z.infer<typeof IndicatorsModel>,
  currentFiscalYear: number,
  sortedAllYears: number[],
): YearGroups => {
  const allTargetYears = data.flatMap((indicator) =>
    indicator.isTargetYear ? indicator.fiscalYear : [],
  );

  return {
    targetPastYears: sortedAllYears.filter(
      (year) => year < currentFiscalYear && allTargetYears.includes(year),
    ),
    targetFutureYears: sortedAllYears.filter(
      (year) => year > currentFiscalYear && allTargetYears.includes(year),
    ),
    latestTargetYear: allTargetYears.at(-1) || currentFiscalYear,
    sortedAllYears,
  };
};

const handleManyTargetYears = (
  yearGroups: YearGroups,
  currentFiscalYear: number,
  isLongTermOutcome: boolean,
): number[] => {
  const { targetPastYears, targetFutureYears, latestTargetYear } = yearGroups;
  const { PAST_YEAR_LENGTH, FUTURE_YEAR_LENGTH, MAX_SHOW_YEARS } =
    LEDGER_YEAR_SETTINGS;

  // 過去3年度以上、未来5年度以上ある場合
  if (
    targetPastYears.length >= PAST_YEAR_LENGTH &&
    targetFutureYears.length >= FUTURE_YEAR_LENGTH
  ) {
    const pastYears = targetPastYears.slice(-1 * PAST_YEAR_LENGTH);
    const futureYears = targetFutureYears.slice(
      0,
      isLongTermOutcome ? FUTURE_YEAR_LENGTH : FUTURE_YEAR_LENGTH + 1,
    );
    return unique([
      ...pastYears,
      currentFiscalYear,
      ...futureYears,
      ...(isLongTermOutcome ? [latestTargetYear] : []),
    ]);
  }

  // 未来年度が5年未満の場合
  if (targetFutureYears.length < FUTURE_YEAR_LENGTH) {
    const showPastYearLength = MAX_SHOW_YEARS - 1 - targetFutureYears.length;

    return unique([
      ...targetPastYears.slice(-1 * showPastYearLength),
      currentFiscalYear,
      ...targetFutureYears,
      latestTargetYear,
    ]);
  }

  // 未来年度が5年以上で過去年度が3年未満の場合
  const showFutureYearLength =
    MAX_SHOW_YEARS -
    (isLongTermOutcome ? LEDGER_YEAR_SETTINGS.DEFAULT_SHOW_YEAR_LENGTH : 1) -
    targetPastYears.length;

  return unique([
    ...targetPastYears,
    currentFiscalYear,
    ...targetFutureYears.slice(0, showFutureYearLength),
    ...(isLongTermOutcome ? [latestTargetYear] : []),
  ]);
};

const handleFewTargetYears = (
  yearGroups: YearGroups,
  currentFiscalYear: number,
  isLongTermOutcome: boolean,
): number[] => {
  const {
    targetPastYears,
    targetFutureYears,
    latestTargetYear,
    sortedAllYears,
  } = yearGroups;
  const { PAST_YEAR_LENGTH, FUTURE_YEAR_LENGTH } = LEDGER_YEAR_SETTINGS;

  const yearsByBeforeCurrentFiscalYear = sortedAllYears.filter(
    (year): year is number => year < currentFiscalYear,
  );

  const yearsByAfterCurrentFiscalYear = sortedAllYears.filter(
    (year): year is number => year > currentFiscalYear,
  );

  const formattedTargetPastYears =
    targetFutureYears.length >= FUTURE_YEAR_LENGTH
      ? targetPastYears.slice(-1 * PAST_YEAR_LENGTH)
      : targetPastYears;

  const newTargetPastYears = fillYearsUpToMaxLength({
    requiredYears: formattedTargetPastYears,
    allYears: yearsByBeforeCurrentFiscalYear.reverse(),
    maxLength: PAST_YEAR_LENGTH,
    lastYear: undefined,
  });

  const pastYearsWithCurrent = unique([
    ...newTargetPastYears,
    currentFiscalYear,
  ]);

  const newTargetFutureYears = fillYearsUpToMaxLength({
    requiredYears: targetFutureYears,
    // 長期の場合は最終目標年度まで、中期・短期の場合はその限りではない
    allYears: yearsByAfterCurrentFiscalYear,
    lastYear: isLongTermOutcome ? latestTargetYear : undefined,
    maxLength:
      LEDGER_YEAR_SETTINGS.MAX_SHOW_YEARS - pastYearsWithCurrent.length,
  });

  return unique([
    ...pastYearsWithCurrent,
    ...newTargetFutureYears,
    latestTargetYear,
  ]).sort((a, b) => a - b);
};

type GetLedgerViewYearsParams = {
  data: z.infer<typeof IndicatorsModel>;
  currentFiscalYear: number;
  sortedAllYears: number[];
  isOutcome: boolean;
  indicatorTerm: TermType | null;
};

const getLedgerViewYears = ({
  data,
  currentFiscalYear,
  sortedAllYears,
  isOutcome,
  indicatorTerm,
}: GetLedgerViewYearsParams): number[] => {
  const yearGroups = getYearGroups(data, currentFiscalYear, sortedAllYears);
  const allTargetYears = data.flatMap((indicator) =>
    indicator.isTargetYear ? indicator.fiscalYear : [],
  );

  const isLongTermOutcome = isOutcome && indicatorTerm === 'long';

  return allTargetYears.length >= LEDGER_YEAR_SETTINGS.MAX_SHOW_YEARS
    ? handleManyTargetYears(yearGroups, currentFiscalYear, isLongTermOutcome)
    : handleFewTargetYears(yearGroups, currentFiscalYear, isLongTermOutcome);
};

type ActivityAndResultTargetTableDataSourceArgs = {
  data: z.infer<typeof IndicatorsModel>;
  indicatorType: IndicatorType;
  indicatorTerm: TermType | null;
  indicatorEvaluationDirection: EvaluationDirectionType | null | undefined;
  indicatorUnit: string | null;
  fiscalYear: number | undefined;
};

export const getActivityAndResultTargetTableDataSource = ({
  data,
  indicatorEvaluationDirection,
  indicatorType,
  indicatorTerm,
  indicatorUnit,
  fiscalYear = getCurrentFiscalYear(),
}: ActivityAndResultTargetTableDataSourceArgs) => {
  const isOutcome = indicatorType === 'outcome';

  // TODO: objectの形にリファクタしたい
  const labels = [
    {
      label: indicatorUnit
        ? t('indicator-target-value-with-unit', { unit: indicatorUnit })
        : t('indicator-target-value'),
    },
    {
      label: indicatorUnit
        ? t('indicator-actual-value-with-unit', { unit: indicatorUnit })
        : t('indicator-actual-value'),
    },
    ...(isOutcome ? [{ label: t('achievement-rate-with-unit') }] : []),
  ];

  const tableDataSource = data.reduce<Array<TableDataType>>((acc, cur) => {
    const { fiscalYear, targetValue, actualValue, achievementRate } = cur;
    // 当初見込み／目標値(%)
    if (acc[0]) {
      acc[0][fiscalYear] = formatNumber(targetValue);
    }

    // 活動実績／成果実績(%)
    if (acc[1]) {
      acc[1][fiscalYear] = formatNumber(actualValue);
    }

    // 達成率(%)
    if (isOutcome && acc[2]) {
      acc[2][fiscalYear] = achievementRate
        ? formatNumber(achievementRate, {
            maximumFractionDigits: 1,
          })
        : rateByEvaluationDirection({
            actualValue,
            targetValue,
            evaluationDirection: indicatorEvaluationDirection,
          });
    }

    return acc;
  }, labels);

  const sortedAllYears = data
    .map((indicator) => indicator.fiscalYear)
    .sort((a, b) => a - b);

  const ledgerViewYears = getLedgerViewYears({
    data,
    currentFiscalYear: fiscalYear,
    sortedAllYears,
    isOutcome,
    indicatorTerm,
  });

  return {
    tableDataSource,
    ledgerViewYears,
    projectViewYears: sortedAllYears,
  };
};

export const parseTargetFiscalYearTitle = ({
  term,
  isLastTargetYear,
  isTargetYear,
  dataNatureType,
}: {
  term: TermType | null | undefined;
  isLastTargetYear: boolean;
  isTargetYear: boolean | undefined;
  dataNatureType: z.infer<typeof dataNatureTypeEnum> | null;
}): string | undefined => {
  if (dataNatureType === 'qualitative') {
    return undefined;
  }
  if (term === 'long' && isLastTargetYear) {
    return t('finally-target-fiscal-year');
  }
  if (isTargetYear) {
    return t('target-fiscal-year');
  }
  return undefined;
};

export const getActivityAndResultTargetTableDataYearText = ({
  year,
  indicatorType,
  term,
  yearsByIsTarget,
  dataNatureType,
}: {
  year: number;
  indicatorType: IndicatorType | undefined;
  term: TermType | null | undefined;
  yearsByIsTarget: number[];
  dataNatureType: z.infer<typeof dataNatureTypeEnum> | null;
}): string => {
  const yearText = t('fiscal-year-with-value', {
    year,
  });
  if (indicatorType === 'output') {
    return yearText;
  }
  const isTargetYear = yearsByIsTarget.includes(year);
  const isLastTargetYear = yearsByIsTarget.at(-1) === year;
  const prefix = parseTargetFiscalYearTitle({
    term,
    isLastTargetYear,
    isTargetYear,
    dataNatureType,
  });
  if (prefix) {
    return `${prefix}\n${yearText}`;
  }
  return yearText;
};

export const parseTreeToNodesAndEdges = (
  tree: z.infer<typeof ActivityFlowModel> | undefined,
) => {
  return {
    nodes:
      tree?.nodes.map((node, index) => ({
        id: node.id,
        position: node.position,
        type: 'customNode',
        data: {
          node,
          count: index + 1,
        },
      })) ?? [],

    edges:
      tree?.edges.map((edge) => ({
        id: edge.id,
        source: edge.sourceNodeId,
        target: edge.targetNodeId,
        data: {
          edgeLabel: edge.label,
        },
      })) ?? [],
  };
};
